import { createFileRoute } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { adminCompaniesOptions, adminAppConfigOptions } from '../lib/resources/queries';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { EmptyState } from '../components/ui/empty-state';

export const Route = createFileRoute('/admin/companies')({
  loader: ({ context: { queryClient } }) => Promise.all([
    queryClient.ensureQueryData(adminCompaniesOptions()),
    queryClient.ensureQueryData(adminAppConfigOptions()),
  ]),
  component: AdminCompaniesPage,
});

function AdminCompaniesPage() {
  const companies = useSuspenseQuery(adminCompaniesOptions()).data;
  const cfg = useSuspenseQuery(adminAppConfigOptions()).data;
  const aggregator = companies.find((c) => c.id === cfg.currentAggregatorCompanyId);
  const recipient = companies.find((c) => c.id === cfg.currentRecipientCompanyId);

  return (
    <div className="max-w-4xl mx-auto p-8 space-y-4">
      <h1 className="text-[28px] font-bold tracking-tight">Companies</h1>

      <Card>
        <CardHeader><CardTitle>Current pairing</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-6 text-[13px]">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-foreground-muted font-medium">Issuer (aggregator)</div>
            <div className="text-[15px] font-medium mt-1">{aggregator?.name ?? '—'}</div>
            <div className="text-foreground-muted text-[12px] mt-0.5">
              Master prefix: <span className="font-mono">{cfg.masterInvoicePrefix}</span> · Counter: {cfg.masterInvoiceCounterYear}/{cfg.masterInvoiceCounterValue}
            </div>
            <div className="text-foreground-muted text-[12px] mt-0.5">
              Template: <span className="font-mono">{aggregator?.masterTemplateKey ?? '—'}</span>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-foreground-muted font-medium">Recipient</div>
            <div className="text-[15px] font-medium mt-1">{recipient?.name ?? '—'}</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>All companies</CardTitle></CardHeader>
        <CardContent>
          {companies.length === 0 ? (
            <EmptyState icon={Building2} title="No companies"
              description="Companies are seeded via Flyway. Check V1__init.sql." />
          ) : (
            <ul className="divide-y divide-border">
              {companies.map((c) => (
                <li key={c.id} className="flex items-start gap-4 py-3 text-[13px]">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{c.name}</div>
                    {c.address && (
                      <div className="text-foreground-muted text-[12px] mt-0.5 whitespace-pre-line">{c.address}</div>
                    )}
                    {c.email && (
                      <div className="text-foreground-muted text-[12px] mt-0.5">{c.email}</div>
                    )}
                  </div>
                  <span className="font-mono text-[12px] text-foreground-muted w-16 text-right shrink-0">{c.currency}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
